import React, { useRef } from 'react';

import { FileUploader } from 'react-drag-drop-files';
import styled from 'styled-components';
import Button from '../../SmallComponents/Button';

type Props = {
    allowedFileTypes?: string[];
    updateFiles: (files: File[] | File) => void;
    multiple?: boolean;
};

const UploadDnD = React.forwardRef(({ allowedFileTypes, updateFiles, multiple }: Props, ref: React.Ref<HTMLDivElement>) => {
    const hiddenFileInputRef = useRef<HTMLInputElement>(null);

    const handleChange = (files: File[] | File) => {
        // if array of files
        if (Array.isArray(files)) {
            updateFiles([...files]);
        } else {
            updateFiles(files);
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            if (multiple) {
                handleChange(Array.from(files));
            } else {
                handleChange(files[0]);
            }
        }
    };

    const handleBrowseClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        hiddenFileInputRef.current?.click();
    };

    return (
        <div ref={ref}>
            <FileUploader 
                multiple={multiple} 
                handleChange={handleChange} 
                name="file"
            >
                <UploadFileContainer>
                    <UploadContent>
                        <UploadIcon>📁</UploadIcon>
                        <UploadText>Drag and drop files here</UploadText>
                        <OrText>or</OrText>
                    </UploadContent>
                </UploadFileContainer>
            </FileUploader>
            
            {/* Hidden file input for browse button */}
            <HiddenFileInput
                ref={hiddenFileInputRef}
                type="file"
                multiple={multiple}
                accept={allowedFileTypes?.join(',')}
                onChange={handleFileInputChange}
            />
            
            <BrowseButtonContainer>
                <BrowseButton 
                    buttonType="hollow" 
                    width="auto" 
                    padding="8px 16px"
                    onClick={handleBrowseClick}
                >
                    Browse Files
                </BrowseButton>
            </BrowseButtonContainer>
        </div>
    );
});

export default UploadDnD;

const UploadFileContainer = styled.div`
    height: 100px;
    width: 100%;
    border: 3px dotted #d9d9d9;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
        border-color: #1890ff;
        background-color: #f6f9ff;
    }
`;

const UploadContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
`;

const UploadIcon = styled.div`
    font-size: 24px;
    opacity: 0.6;
`;

const UploadText = styled.span`
    color: #666;
    font-size: 14px;
    text-align: center;
`;

const OrText = styled.span`
    color: #999;
    font-size: 12px;
    margin: 4px 0;
`;

const BrowseButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 12px;
`;

const HiddenFileInput = styled.input`
    display: none;
`;

const BrowseButton = styled(Button)`
    font-size: 12px;
    height: 32px;
    
    &:hover {
        transform: none;
    }
`;
